import { SwitchWithLabel } from '@newfront-insurance/core-ui';
import { useState } from 'react';

import { SettingsCard } from './settings-card';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';

/**
 * Notification settings component that displays a toggle switch for enabling/disabling browser notifications.
 * Uses the notificationController from context to manage notification state.
 */
export function NotificationSettings() {
  const { notificationController } = useChatOverlayContext();
  const [isLoading, setIsLoading] = useState(false);

  const {
    browserNotificationsEnabled,
    browserNotificationPermission,
    browserNotificationsSupported,
    enableBrowserNotifications,
    disableBrowserNotifications,
  } = notificationController;

  const handleToggle = async () => {
    if (!browserNotificationsSupported) {
      return;
    }

    setIsLoading(true);
    try {
      if (browserNotificationsEnabled) {
        disableBrowserNotifications();
      } else {
        const success = await enableBrowserNotifications();

        if (!success && browserNotificationPermission === 'denied') {
          // eslint-disable-next-line no-console
          console.warn('[Nova Notifications] Notifications are blocked. Please enable them in your browser settings.');
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if browser doesn't support notifications
  if (!browserNotificationsSupported) {
    return null;
  }

  const isDisabled = isLoading || browserNotificationPermission === 'denied';

  return (
    <SettingsCard
      title="Notifications"
      action={
        <SwitchWithLabel
          label=""
          enabled={browserNotificationsEnabled}
          onClick={handleToggle}
          locked={isDisabled}
          labelPosition="left"
        />
      }
    >
      <p className="text-muted-foreground text-sm">Get notified when your chat conversations receive new messages.</p>
      {browserNotificationPermission === 'denied' && (
        <p className="text-destructive mt-2 text-sm">
          Notifications are blocked by your browser. Please enable them in your browser settings to receive
          notifications.
        </p>
      )}
    </SettingsCard>
  );
}
